import {getExtraSetting, saveExtraSetting} from '../../../../../../_metronic/helpers'
import {AuthModel} from '../../../../auth/core/_models'

export const INVOICE_LIST_SEASON_ALL_KEY = 'all'
export const INVOICE_LIST_SEASON_SETTING_NAME = 'invoiceListSeasonSelection'

export type InvoiceSeasonUserSetting = {
  invoiceSeason?: string
  baseMode?: number
  companyId?: number
  selections?: Record<string, string>
}

type InvoiceSeasonUser = Pick<AuthModel, 'id' | 'cpi'> | undefined

const getScopeKey = (currentUser: InvoiceSeasonUser, baseMode?: number) =>
  `${currentUser?.cpi || 0}:${baseMode || 1}`

const getCacheKey = (currentUser: InvoiceSeasonUser, baseMode?: number) =>
  `MegaPay.invoiceListSeason.${currentUser?.id || 0}.${getScopeKey(currentUser, baseMode)}`

const canUseLocalStorage = () => typeof window !== 'undefined' && !!window.localStorage

export const normalizeInvoiceSeasonValue = (value?: unknown): string => {
  if (Array.isArray(value)) {
    return normalizeInvoiceSeasonValue(value.join(','))
  }

  const rawValue = `${value ?? ''}`.trim()
  if (!rawValue) {
    return ''
  }

  const parts = rawValue
    .split(/[,\|;]+/)
    .map((item) => item.trim())
    .filter(Boolean)

  if (parts.some((item) => item.toLowerCase() === INVOICE_LIST_SEASON_ALL_KEY)) {
    return INVOICE_LIST_SEASON_ALL_KEY
  }

  return Array.from(new Set(parts)).join(',')
}

export const isAllInvoiceSeasonValue = (value?: unknown) =>
  normalizeInvoiceSeasonValue(value) === INVOICE_LIST_SEASON_ALL_KEY

export const splitInvoiceSeasonValue = (value?: unknown): string[] => {
  const normalized = normalizeInvoiceSeasonValue(value)
  if (!normalized || normalized === INVOICE_LIST_SEASON_ALL_KEY) {
    return []
  }

  return normalized.split(',').filter(Boolean)
}

export const isMultiInvoiceSeasonValue = (value?: unknown) =>
  splitInvoiceSeasonValue(value).length > 1

export const getInvoiceSeasonFromSetting = (
  setting: InvoiceSeasonUserSetting | string | null | undefined,
  currentUser: InvoiceSeasonUser,
  baseMode?: number
) => {
  if (!setting) {
    return ''
  }

  if (typeof setting === 'string') {
    return normalizeInvoiceSeasonValue(setting)
  }

  const scopeValue = setting.selections?.[getScopeKey(currentUser, baseMode)]
  return normalizeInvoiceSeasonValue(scopeValue || setting.invoiceSeason)
}

export const buildInvoiceSeasonSetting = (
  setting: InvoiceSeasonUserSetting | string | null | undefined,
  currentUser: InvoiceSeasonUser,
  baseMode: number | undefined,
  invoiceSeason: string
): InvoiceSeasonUserSetting => {
  const normalized = normalizeInvoiceSeasonValue(invoiceSeason)
  const previousSetting = typeof setting === 'object' && setting ? setting : {}
  const selections = {...(previousSetting.selections || {})}
  selections[getScopeKey(currentUser, baseMode)] = normalized

  return {
    ...previousSetting,
    invoiceSeason: normalized,
    baseMode: baseMode || 1,
    companyId: currentUser?.cpi || 0,
    selections,
  }
}

export const readCachedInvoiceSeasonSelection = (
  currentUser: InvoiceSeasonUser,
  baseMode?: number
) => {
  if (!canUseLocalStorage()) {
    return ''
  }

  return normalizeInvoiceSeasonValue(window.localStorage.getItem(getCacheKey(currentUser, baseMode)))
}

export const cacheInvoiceSeasonSelection = (
  currentUser: InvoiceSeasonUser,
  baseMode: number | undefined,
  invoiceSeason: string
) => {
  if (!canUseLocalStorage()) {
    return
  }

  const normalized = normalizeInvoiceSeasonValue(invoiceSeason)
  if (normalized) {
    window.localStorage.setItem(getCacheKey(currentUser, baseMode), normalized)
  } else {
    window.localStorage.removeItem(getCacheKey(currentUser, baseMode))
  }
}

export const loadInvoiceSeasonUserSetting = async () => {
  return (await getExtraSetting(INVOICE_LIST_SEASON_SETTING_NAME)) as
    | InvoiceSeasonUserSetting
    | string
    | null
    | undefined
}

export const persistInvoiceSeasonSelection = async (
  currentUser: InvoiceSeasonUser,
  baseMode: number | undefined,
  invoiceSeason: string,
  knownSetting?: InvoiceSeasonUserSetting | string | null
) => {
  const normalized = normalizeInvoiceSeasonValue(invoiceSeason)
  cacheInvoiceSeasonSelection(currentUser, baseMode, normalized)

  if (!currentUser?.id) {
    return undefined
  }

  const setting = knownSetting === undefined ? await loadInvoiceSeasonUserSetting() : knownSetting
  const nextSetting = buildInvoiceSeasonSetting(setting, currentUser, baseMode, normalized)
  await saveExtraSetting(INVOICE_LIST_SEASON_SETTING_NAME, nextSetting)
  return nextSetting
}
